import { computed, readonly, ref, watch } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'system'
export const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system']

export const THEME_STORAGE_KEY = 'it-tools:theme'

/**
 * Inline this in <head> before the first paint so the page never flashes the wrong
 * theme. It is duplicated (rather than imported) on purpose: it has to run before
 * any bundle loads.
 */
export const THEME_BOOTSTRAP_SCRIPT = `(function(){try{var m=localStorage.getItem('${THEME_STORAGE_KEY}')||'system';var d=m==='dark'||(m==='system'&&matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.dataset.theme=d?'dark':'light'}catch(e){document.documentElement.dataset.theme='light'}})()`

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return THEME_MODES.includes(stored as ThemeMode) ? (stored as ThemeMode) : 'system'
  } catch {
    // Private browsing can throw on access; fall back rather than break the app.
    return 'system'
  }
}

const mode = ref<ThemeMode>(readStoredMode())
const systemPrefersDark = ref(false)

let query: MediaQueryList | null = null
function ensureSystemWatcher(): void {
  if (query || typeof window === 'undefined') return
  query = window.matchMedia('(prefers-color-scheme: dark)')
  systemPrefersDark.value = query.matches
  query.addEventListener('change', (event) => {
    systemPrefersDark.value = event.matches
  })
}

/** Mirrors the resolved theme onto <html data-theme> whenever it changes. */
function applyResolvedTheme(theme: 'light' | 'dark'): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = theme
  }
}

const resolvedTheme = computed<'light' | 'dark'>(() => {
  if (mode.value === 'system') return systemPrefersDark.value ? 'dark' : 'light'
  return mode.value
})

// One app-lifetime watcher keeps the DOM in sync, including when the OS preference
// flips while the user sits in `system` mode.
watch(resolvedTheme, applyResolvedTheme, { immediate: true })

/**
 * Theme state, shared across every component that calls it.
 *
 * Three modes: explicit `light`, explicit `dark`, or `system` (the default), which
 * follows the OS and keeps following it as the OS changes.
 */
export function useTheme() {
  ensureSystemWatcher()

  function setMode(next: ThemeMode): void {
    mode.value = next
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Persistence is a nicety, not a requirement.
    }
  }

  /** Cycles light -> dark -> system, for a single-button switcher. */
  function cycleMode(): void {
    const index = THEME_MODES.indexOf(mode.value)
    setMode(THEME_MODES[(index + 1) % THEME_MODES.length] ?? 'system')
  }

  return {
    mode: readonly(mode),
    resolved: resolvedTheme,
    isDark: computed(() => resolvedTheme.value === 'dark'),
    setMode,
    cycleMode,
  }
}
